import React, { useCallback, useEffect, useRef, useState } from "react";
import "./OnboardingPage.css";

// "Practices needing linking" (docs/onboarding.md): the admin side of practice
// onboarding. Cross-practice like Mission Control, so it takes no selectedPractice.
// Mint intake links, work each practice's feed checklist (download the discovery prompt,
// upload the discovery output, run first pulls), enable the practice (super_admin).
//
// Two kinds of practice sit in the table: ones that came in through an intake link
// ("New intake") and existing practices with a required feed still to link
// ("Existing"); "Show all practices" lists every practice so any can be opened.

const API_URL = process.env.REACT_APP_API_URL || "";

const STATUS_LABEL = {
  not_linked: "Not linked",
  configured: "Configured",
  first_pull_ok: "First pull ok",
  failing: "Failing",
};

const ORIGIN_LABEL = {
  new_intake: "New intake",
  existing: "Existing",
};

async function readJson(resp) {
  try {
    return await resp.json();
  } catch (_) {
    return {};
  }
}

function errorOf(resp, body) {
  return (body && body.error) || `HTTP ${resp.status}`;
}

// A configured feed with no connector is a different thing from one waiting on its
// first pull: the discovery output named a source, but nobody has built the connector
// yet. It gets its own badge so the two never read the same. A feed the catalog marks
// "missing" (no source on this EMR at all: the 835s of an eCW or PracticeSuite practice
// without a clearinghouse key) is a third thing, and reads "No source".
function StatusBadge({ status, connector, catalogStatus }) {
  // The catalog's "missing" speaks only for an untouched row: once discovery names a
  // source (configured) or a pull has run, the row's own state is shown.
  if (!connector && catalogStatus === "missing" && status === "not_linked") {
    return (
      <span className="status-badge onboarding-status-missing">No source</span>
    );
  }
  if (status === "configured" && !connector) {
    return (
      <span className="status-badge onboarding-status-to_build">
        Configured, to build
      </span>
    );
  }
  return (
    <span className={`status-badge onboarding-status-${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// Two lines under the count: which feeds are pulling (with the required fields their
// connector cannot supply, where it is partial) and which still wait on the discovery
// session, for the whole feed or for those fields. A feed can be on both lines.
function DiscoverySummary({ summary }) {
  const linked = summary.linked || [];
  const waiting = summary.awaiting_discovery || [];
  if (!linked.length && !waiting.length) return null;
  const linkedText = linked
    .map((l) => {
      const rows = l.last_rows == null ? "" : ` (${l.last_rows} rows)`;
      const missing =
        l.missing_fields && l.missing_fields.length
          ? `, without ${l.missing_fields.join(", ")}`
          : "";
      return `${l.label}${rows}${missing}`;
    })
    .join("; ");
  const waitingText = waiting
    .map((w) =>
      w.needed === "feed"
        ? `${w.label} (whole feed)`
        : `${w.label} (${(w.fields || []).join(", ")})`,
    )
    .join("; ");
  return (
    <div className="onboarding-discovery-summary">
      <div className="onboarding-feed-detail onboarding-linked-line">
        <strong>Pulling:</strong> {linked.length ? linkedText : "nothing yet"}
      </div>
      <div className="onboarding-feed-detail onboarding-waiting-line">
        <strong>Waiting on the discovery session:</strong>{" "}
        {waiting.length
          ? waitingText
          : "nothing; every feed is pulling with every required field"}
      </div>
    </div>
  );
}

function FeedRow({ row, practiceId, onPulled, onMessage, busy, setBusy }) {
  const runFirstPull = async () => {
    setBusy(true);
    onMessage(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/onboarding/practices/${practiceId}/feeds/${row.feed_kind}/first-pull`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
      );
      const body = await readJson(resp);
      if (!resp.ok) throw new Error(errorOf(resp, body));
      if (body.queued === false) {
        onMessage({
          type: "warning",
          text: body.reason,
          command: body.command,
          how: body.how,
        });
      } else if (body.ok) {
        onMessage({
          type: "success",
          text: `${row.label}: first pull ok, ${body.rows == null ? "rows unknown" : `${body.rows} rows`}.`,
        });
      } else {
        onMessage({
          type: "error",
          text: `${row.label}: first pull failed. ${(body.link && body.link.last_error) || ""}`,
        });
      }
      onPulled();
    } catch (err) {
      onMessage({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const fp = row.first_pull || {};
  const missingFields = row.missing_fields || [];
  return (
    <tr className="onboarding-feed-row">
      <td>
        <div className="onboarding-feed-label">{row.label}</div>
        <div className="onboarding-feed-detail">{row.status_detail}</div>
        {missingFields.length > 0 && (
          <div className="onboarding-feed-detail onboarding-missing-fields">
            cannot supply: {missingFields.join(", ")}
          </div>
        )}
        {row.discovery && (
          <div className="onboarding-feed-detail onboarding-discovery">
            {row.discovery.needed === "feed"
              ? "waits on the discovery session: the whole feed"
              : `waits on the discovery session for: ${(row.discovery.fields || []).join(", ")}`}
          </div>
        )}
        {row.patient_identifier_type && (
          <div className="onboarding-feed-detail">
            patient id: {row.patient_identifier_type}
          </div>
        )}
        {row.routes && (
          <div className="onboarding-feed-detail onboarding-routes">
            {row.routes}
          </div>
        )}
        {row.provenance === "backfilled_from_data" && (
          <div className="onboarding-feed-detail onboarding-provenance">
            link row backfilled from the feed's own data
          </div>
        )}
      </td>
      <td>{row.required ? "Required" : "Optional"}</td>
      <td className="onboarding-powers">{(row.powers || []).join(", ")}</td>
      <td>
        <code>{row.source}</code>
      </td>
      <td>
        {row.route === "atlas_is_pm" && (
          <span
            className="onboarding-route"
            title="Atlas is this practice's PM"
          >
            Atlas is the PM
          </span>
        )}
        {row.connector ? (
          <code>{row.connector}</code>
        ) : row.catalog_status === "missing" && row.status === "not_linked" ? (
          <span className="onboarding-to-build onboarding-missing">
            no source on this EMR; see routes
          </span>
        ) : (
          <span className="onboarding-to-build">to build</span>
        )}
      </td>
      <td>
        <StatusBadge
          status={row.status}
          connector={row.connector}
          catalogStatus={row.catalog_status}
        />
      </td>
      <td className="onboarding-nowrap">
        {row.last_pull_at ? new Date(row.last_pull_at).toLocaleString() : "—"}
        {row.feed_health && row.feed_health.last_run_at && (
          <div className="onboarding-feed-detail">
            feed health: {row.feed_health.label}, {row.feed_health.status}
          </div>
        )}
      </td>
      <td>
        {row.last_rows == null
          ? row.feed_health && row.feed_health.rows_last_run != null
            ? `${row.feed_health.rows_last_run} seen`
            : "—"
          : row.last_rows}
      </td>
      <td className="onboarding-actions">
        {fp.available && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy}
            onClick={runFirstPull}
          >
            Run first pull
          </button>
        )}
        {!fp.available && fp.command && (
          <div className="onboarding-command">
            <div className="onboarding-feed-detail">
              run this from an operator shell
            </div>
            <code>{fp.command}</code>
          </div>
        )}
        {!fp.available && !fp.command && (
          <span className="onboarding-feed-detail">{fp.how}</span>
        )}
      </td>
    </tr>
  );
}

function Checklist({ practice, isSuperAdmin, onEnabled }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [confirmEnable, setConfirmEnable] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const resp = await fetch(
        `${API_URL}/api/onboarding/practices/${practice.id}/checklist`,
      );
      const body = await readJson(resp);
      if (!resp.ok) throw new Error(errorOf(resp, body));
      setData(body);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [practice.id]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPrompt = async () => {
    setMessage(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/onboarding/practices/${practice.id}/discovery-prompt`,
      );
      if (!resp.ok) {
        const body = await readJson(resp);
        throw new Error(errorOf(resp, body));
      }
      const cd = resp.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = match
        ? decodeURIComponent(match[1])
        : `onboarding-discovery-${practice.id}.md`;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const uploadDiscovery = (file) => {
    if (!file) return;
    setMessage(null);
    setUploadResult(null);
    const reader = new FileReader();
    reader.onerror = () =>
      setMessage({ type: "error", text: "The file could not be read." });
    reader.onload = async () => {
      let payload;
      try {
        payload = JSON.parse(reader.result);
      } catch (_) {
        setMessage({ type: "error", text: "The file is not valid JSON." });
        return;
      }
      setBusy(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/onboarding/practices/${practice.id}/discovery`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        const body = await readJson(resp);
        if (!resp.ok) {
          setMessage({
            type: "error",
            text: errorOf(resp, body),
            errors: body.errors || [],
          });
          return;
        }
        setData(body.checklist);
        setUploadResult(body.to_build || []);
        setMessage({
          type: "success",
          text: `Discovery output applied. ${(body.to_build || []).length} connector(s) to build.`,
        });
      } catch (err) {
        setMessage({ type: "error", text: err.message });
      } finally {
        setBusy(false);
        if (fileInput.current) fileInput.current.value = "";
      }
    };
    reader.readAsText(file);
  };

  const enable = async () => {
    setConfirmEnable(false);
    setBusy(true);
    try {
      const resp = await fetch(
        `${API_URL}/api/onboarding/practices/${practice.id}/enable`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
      );
      const body = await readJson(resp);
      if (!resp.ok) throw new Error(errorOf(resp, body));
      setMessage({
        type: body.warnings && body.warnings.length ? "warning" : "success",
        text: `${practice.name} is enabled.${body.warnings && body.warnings.length ? " Required feeds not yet pulled:" : ""}`,
        errors: body.warnings || [],
      });
      onEnabled();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="form-message error">{error}</div>;
  if (!data)
    return <div className="onboarding-feed-detail">Loading checklist…</div>;

  const pendingRequired = data.rows.filter(
    (r) => r.required && r.status !== "first_pull_ok",
  );
  const systems = data.practice.systems || {};

  return (
    <div className="onboarding-checklist">
      <div className="onboarding-checklist-toolbar">
        <div>
          <span className="onboarding-summary">{data.summary.done_label}</span>
          <div
            className={`onboarding-feed-detail onboarding-rich ${data.summary.rich ? "is-rich" : "not-rich"}`}
          >
            {data.summary.rich
              ? "Rich: the remittances feed (835s or their adjudication columns) is pulling."
              : data.summary.rich_note}
          </div>
          <div className="onboarding-feed-detail">
            {ORIGIN_LABEL[data.practice.origin] || data.practice.origin}
            {systems.how ? ` · ${systems.how}` : ""}
          </div>
          {systems.cobalt_coverage && (
            <div className="onboarding-feed-detail">
              {systems.cobalt_coverage}
            </div>
          )}
          <DiscoverySummary summary={data.summary} />
        </div>
        <div className="onboarding-toolbar-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={downloadPrompt}
          >
            Download discovery prompt
          </button>
          <label className="btn btn-secondary btn-sm onboarding-upload">
            Upload discovery output
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              onChange={(e) =>
                uploadDiscovery(e.target.files && e.target.files[0])
              }
            />
          </label>
          {isSuperAdmin && !data.practice.enabled && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={() => setConfirmEnable(true)}
            >
              Enable practice
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className={`form-message ${message.type} dismissable`}>
          <div className="message-content">
            {message.text}
            {message.command && (
              <div className="onboarding-command">
                <code>{message.command}</code>
              </div>
            )}
            {message.errors && message.errors.length > 0 && (
              <ul className="onboarding-error-list">
                {message.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="message-close"
            onClick={() => setMessage(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {uploadResult && uploadResult.length > 0 && (
        <div className="form-message warning">
          <strong>Connectors to build</strong> (open each as its own PR; see
          docs/onboarding.md):
          <ul className="onboarding-error-list">
            {uploadResult.map((t) => (
              <li key={t.feed_kind}>
                {t.label} — source <code>{t.source}</code>, access{" "}
                <code>{t.access_method}</code>, cadence <code>{t.cadence}</code>
              </li>
            ))}
          </ul>
        </div>
      )}

      <table className="detail-table onboarding-feed-table">
        <thead>
          <tr>
            <th>Feed</th>
            <th>Required</th>
            <th>Powers</th>
            <th>Source</th>
            <th>Connector</th>
            <th>Status</th>
            <th>Last pull</th>
            <th>Rows</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <FeedRow
              key={row.feed_kind}
              row={row}
              practiceId={practice.id}
              onPulled={load}
              onMessage={setMessage}
              busy={busy}
              setBusy={setBusy}
            />
          ))}
        </tbody>
      </table>

      {confirmEnable && (
        <div className="modal-overlay" onClick={() => setConfirmEnable(false)}>
          <div className="create-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Enable {practice.name}?</h2>
            <p>
              The practice appears in the practice picker for every user with
              access to it.
            </p>
            {pendingRequired.length > 0 ? (
              <div className="form-message warning">
                Required feeds not yet pulled:
                <ul className="onboarding-error-list">
                  {pendingRequired.map((r) => (
                    <li key={r.feed_kind}>
                      {r.label}: {STATUS_LABEL[r.status] || r.status}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="form-message success">
                Every required feed has pulled.
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmEnable(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={enable}
              >
                Enable practice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewIntakeModal({ onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/onboarding/intakes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label }),
      });
      const body = await readJson(resp);
      if (!resp.ok) throw new Error(errorOf(resp, body));
      setResult(body);
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
    } catch (_) {
      setCopied(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-modal" onClick={(e) => e.stopPropagation()}>
        <h2>New intake link</h2>
        {error && <div className="form-message error">{error}</div>}
        {!result ? (
          <form onSubmit={create}>
            <div className="form-row">
              <div className="form-field full">
                <label htmlFor="intake-label">
                  Label (internal note, never shown to the practice; e.g. "Dr
                  Smith, Tebra")
                </label>
                <input
                  id="intake-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Creating…" : "Create link"}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="form-message warning">
              This URL is shown <strong>once</strong>. Copy it now and send it
              to the practice; it expires{" "}
              {new Date(result.expires_at).toLocaleDateString()} (14 days) and
              works one time.
            </div>
            <div className="onboarding-url-row">
              <code className="onboarding-url">{result.url}</code>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={copy}
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function OnboardingPage({ isSuperAdmin }) {
  const [practices, setPractices] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showIntakes, setShowIntakes] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Two quick toggles can answer out of order; only the latest request may write.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      const [pr, ir] = await Promise.all([
        fetch(`${API_URL}/api/onboarding/practices${showAll ? "?all=1" : ""}`),
        fetch(`${API_URL}/api/onboarding/intakes`),
      ]);
      const pb = await readJson(pr);
      const ib = await readJson(ir);
      if (!pr.ok) throw new Error(errorOf(pr, pb));
      if (!ir.ok) throw new Error(errorOf(ir, ib));
      if (seq !== requestSeq.current) return; // a newer load has taken over
      setPractices(pb.practices || []);
      setIntakes(ib.intakes || []);
      setError(null);
    } catch (err) {
      if (seq === requestSeq.current) setError(err.message);
    }
  }, [showAll]);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = async (id) => {
    try {
      const resp = await fetch(
        `${API_URL}/api/onboarding/intakes/${id}/revoke`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );
      const body = await readJson(resp);
      if (!resp.ok) throw new Error(errorOf(resp, body));
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-header">
        <div>
          <h1>Practices needing linking</h1>
          <p className="onboarding-muted">
            Intake link → practice submits → discovery → upload → first pulls →
            enable. Existing practices with a required feed still to link are
            listed too. The walkthrough is docs/onboarding.md.
          </p>
        </div>
        <div className="onboarding-toolbar-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Show practices needing linking" : "Show all practices"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowIntakes((v) => !v)}
          >
            {showIntakes
              ? "Hide intake links"
              : `Intake links (${intakes.length})`}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowNew(true)}
          >
            New intake link
          </button>
        </div>
      </div>

      {error && (
        <div className="form-message error dismissable">
          <span className="message-content">{error}</span>
          <button
            type="button"
            className="message-close"
            onClick={() => setError(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {showIntakes && (
        <table className="detail-table onboarding-intakes-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Status</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {intakes.length === 0 && (
              <tr>
                <td colSpan={6} className="onboarding-muted">
                  No intake links yet.
                </td>
              </tr>
            )}
            {intakes.map((i) => (
              <tr key={i.id}>
                <td>{i.label || "—"}</td>
                <td>
                  <span
                    className={`status-badge onboarding-intake-${i.status}`}
                  >
                    {i.status}
                  </span>
                </td>
                <td>
                  {i.created_at
                    ? new Date(i.created_at).toLocaleDateString()
                    : "—"}
                </td>
                <td>
                  {i.expires_at
                    ? new Date(i.expires_at).toLocaleDateString()
                    : "—"}
                </td>
                <td>
                  {i.used_at ? new Date(i.used_at).toLocaleDateString() : "—"}
                </td>
                <td className="onboarding-actions">
                  {i.status === "open" && (
                    <button
                      type="button"
                      className="btn-link danger"
                      onClick={() => revoke(i.id)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {practices === null ? (
        <div className="onboarding-muted">Loading…</div>
      ) : practices.length === 0 ? (
        <div className="onboarding-empty">
          {showAll
            ? "No practices."
            : "Every practice has its required feeds linked. Mint an intake link to start a new one, or show all practices."}
        </div>
      ) : (
        <table className="detail-table onboarding-practices-table">
          <thead>
            <tr>
              <th></th>
              <th>Practice</th>
              <th>Origin</th>
              <th>EMR / PM</th>
              <th>Intake submitted</th>
              <th>Required done</th>
              <th>To build</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {practices.map((p) => (
              <React.Fragment key={p.id}>
                <tr
                  className="onboarding-practice-row"
                  onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                >
                  <td className="onboarding-expander">
                    {expanded === p.id ? "▾" : "▸"}
                  </td>
                  <td className="col-name">{p.name}</td>
                  <td>
                    <span
                      className={`status-badge onboarding-origin-${p.origin}`}
                    >
                      {ORIGIN_LABEL[p.origin] || p.origin}
                    </span>
                  </td>
                  <td>
                    {p.emr_system || "—"}
                    {p.pm_system && p.pm_system !== p.emr_system
                      ? ` / ${p.pm_system}`
                      : ""}
                    {p.clearinghouse ? ` · ${p.clearinghouse}` : ""}
                  </td>
                  <td>
                    {p.intake_submitted_at
                      ? new Date(p.intake_submitted_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>
                    {p.required_done} / {p.required_total}
                  </td>
                  <td>{p.feeds_to_build}</td>
                  <td>
                    <span
                      className={`status-badge ${p.feeds_failing ? "onboarding-status-failing" : p.required_done === p.required_total ? "onboarding-status-first_pull_ok" : "onboarding-status-not_linked"}`}
                    >
                      {p.feeds_failing
                        ? `${p.feeds_failing} failing`
                        : p.onboarding_status ||
                          (p.enabled ? "enabled" : "disabled")}
                    </span>
                  </td>
                </tr>
                {expanded === p.id && (
                  <tr className="onboarding-detail-row">
                    <td colSpan={8}>
                      <Checklist
                        practice={p}
                        isSuperAdmin={isSuperAdmin}
                        onEnabled={load}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <NewIntakeModal onClose={() => setShowNew(false)} onCreated={load} />
      )}
    </div>
  );
}

export default OnboardingPage;
